import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

export function useAuth() {
  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [isAdmin, setIsAdmin] = useState(false);

  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const [forgotEmail, setForgotEmail] = useState("");

  const [authMode, setAuthMode] = useState("login");
  const [signupUsername, setSignupUsername] = useState("");
  const [signupFullName, setSignupFullName] = useState("");

  const [accessTypes, setAccessTypes] = useState([]);
  const [canAccessFreePractical, setCanAccessFreePractical] = useState(false);
  const [canAccessSubjectQuestions, setCanAccessSubjectQuestions] =
    useState(false);
  const [canAccessFederalExam, setCanAccessFederalExam] = useState(false);

  const resetAccessState = () => {
    setAccessTypes([]);
    setCanAccessFreePractical(false);
    setCanAccessSubjectQuestions(false);
    setCanAccessFederalExam(false);
  };

  const checkQuestionAccess = async (userId, isSuperUser) => {
    if (isSuperUser) {
      setAccessTypes(["all_access"]);
      setCanAccessFreePractical(true);
      setCanAccessSubjectQuestions(true);
      setCanAccessFederalExam(true);
      return;
    }

    const today = new Date().toISOString().slice(0, 10);

    const { data, error } = await supabase
      .from("question_bank_access")
      .select("access_type, access_start_date, access_end_date, access_status")
      .eq("user_id", userId)
      .eq("access_status", "active")
      .lte("access_start_date", today)
      .gte("access_end_date", today);

    if (error) {
      console.error("ACCESS ERROR:", error);
      setAccessTypes([]);
      setCanAccessFreePractical(true);
      setCanAccessSubjectQuestions(false);
      setCanAccessFederalExam(false);
      return;
    }

    const types = (data || []).map((row) => row.access_type);

    setAccessTypes(types);
    setCanAccessFreePractical(true);
    setCanAccessSubjectQuestions(
      types.includes("subject_questions") || types.includes("all_access")
    );
    setCanAccessFederalExam(
      types.includes("federal_exam") || types.includes("all_access")
    );
  };

  const checkAdminStatus = async (userId) => {
    const { data, error } = await supabase
      .from("user_profiles")
      .select("role, status")
      .eq("user_id", userId)
      .single();

    if (error) {
      console.error("PROFILE ERROR:", error);
      setIsAdmin(false);
      await checkQuestionAccess(userId, false);
      return;
    }

    const adminFlag = data?.role === "superuser" && data?.status === "active";

    setIsAdmin(adminFlag);
    await checkQuestionAccess(userId, adminFlag);
  };

  const handleLogin = async () => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      alert(error.message);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
    resetAccessState();
  };

  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;

    if (hash.includes("type=recovery") || search.includes("type=recovery")) {
      setIsPasswordRecovery(true);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const {
          data: { session },
          error,
        } =
          await supabase.auth.getSession();

        if (!mounted) {
          return;
        }

        if (error) {
          console.error(
            "GET SESSION ERROR:",
            error
          );
        }

        setSession(session);

        if (session?.user) {
          await checkAdminStatus(
            session.user.id
          );
        } else {
          setIsAdmin(false);
          resetAccessState();
        }
      } catch (error) {
        console.error(
          "AUTH INITIALIZATION ERROR:",
          error
        );
      } finally {
        if (mounted) {
          setAuthReady(true);
        }
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } =
      supabase.auth.onAuthStateChange(
        (event, session) => {
          if (
            event ===
            "PASSWORD_RECOVERY"
          ) {
            setIsPasswordRecovery(
              true
            );
          }

          setSession(session);

          if (session?.user) {
            checkAdminStatus(
              session.user.id
            );
          } else {
            setIsAdmin(false);
            resetAccessState();
          }
        }
      );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdatePassword = async () => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      alert(error.message);
      return;
    }

    alert("Password updated successfully.");

    setIsPasswordRecovery(false);
    setNewPassword("");

    await supabase.auth.signOut();
  };

  const handleForgotPassword = async () => {
    const resetEmail = forgotEmail || email;

    if (!resetEmail) {
      alert("Please enter your email first.");
      return;
    }

    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: redirectUrl,
    });

    if (error) {
      console.error("PASSWORD RESET ERROR:", error);
      alert(error.message);
      return;
    }

    alert("Password reset email sent. Please check your inbox.");
  };

  const handleSignup = async () => {
    if (!email || !password || !signupUsername || !signupFullName) {
      alert("Please fill in all signup fields.");
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) {
      alert(error.message);
      return;
    }

    const userId = data.user?.id;

    if (!userId) {
      alert("Signup created. Please check your email to confirm.");
      return;
    }

    const { error: profileError } = await supabase.from("user_profiles").insert({
      user_id: userId,
      username: signupUsername,
      full_name: signupFullName,
      email,
      role: "student",
      status: "active",
    });

    if (profileError) {
      alert(profileError.message);
      return;
    }

    alert("Signup successful. You can now log in.");

    setAuthMode("login");
    setSignupUsername("");
    setSignupFullName("");
    setPassword("");
  };

  return {
    session,
    authReady,

    email,
    setEmail,

    password,
    setPassword,

    isAdmin,
    handleLogin,
    handleLogout,

    isPasswordRecovery,
    newPassword,
    setNewPassword,
    handleUpdatePassword,

    forgotEmail,
    setForgotEmail,
    handleForgotPassword,

    authMode,
    setAuthMode,
    signupUsername,
    setSignupUsername,
    signupFullName,
    setSignupFullName,
    handleSignup,
    accessTypes,
    canAccessFreePractical,
    canAccessSubjectQuestions,
    canAccessFederalExam,
  };
}